import { useEffect, useRef } from 'react';
import { searchBoard, tasksForDay } from '../../api/board';
import { useApp } from '../../context/AppContext';
import { useI18n } from '../../i18n/useI18n';
import { matchFolded } from '../../utils/text';
import { useUi } from '../../context/UiContext';
import { useDismiss } from '../../hooks/useDismiss';
import { daysBetween, formatDateLabel, formatWeekday, fromKey } from '../../utils/dates';
import { nextOccurrence, recurrenceLabel } from '../../utils/recurrence';
import { focusSelector } from '../../utils/focus';
import { IconSearch } from '../icons/Icons';
import styles from './overlays.module.css';

function Highlight({ text, query }) {
  const range = matchFolded(text, query);
  if (!range) return text;
  const [start, end] = range;
  return (
    <>
      {text.slice(0, start)}
      <mark>{text.slice(start, end)}</mark>
      {text.slice(end)}
    </>
  );
}

export function SearchPopover({ anchorRef }) {
  const { state, actions } = useApp();
  const { lang, t } = useI18n();
  const { ui, setQuery, close } = useUi();
  const popRef = useRef(null);
  const inputRef = useRef(null);
  useDismiss(true, () => close('searchOpen'), [popRef, anchorRef]);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const results = searchBoard(state, ui.query);
  const dayResults = results.filter((result) => result.kind === 'day');
  const listResults = results.filter((result) => result.kind === 'list');
  const recurResults = results.filter((result) => result.kind === 'recur');

  const openResult = (result) => {
    if (result.task.done && !state.preferences.showCompleted) {
      actions.setPref('showCompleted', true);
    }
    if (result.kind === 'recur') {
      const date = nextOccurrence(result.item, new Date());
      const occurs = tasksForDay(
        {
          ...state,
          preferences: { ...state.preferences, showCompleted: true },
        },
        date
      );
      const index = occurs.findIndex((task) => task.id === result.item.id);
      const done = (result.item.doneOn || []).includes(date);
      if (done && !state.preferences.showCompleted) actions.setPref('showCompleted', true);
      actions.setOffset(daysBetween(new Date(), fromKey(date)));
      close('searchOpen');
      focusSelector(`[data-focus="day-${date}:${index}"]`);
      return;
    }
    if (result.kind === 'day') {
      actions.setOffset(daysBetween(new Date(), fromKey(result.date)));
      close('searchOpen');
      focusSelector(`[data-focus="day-${result.date}:${result.index}"]`);
      return;
    }
    actions.selectPage(result.pageId);
    actions.setListsExpanded(true);
    close('searchOpen');
    focusSelector(`[data-focus="list-${result.listId}:${result.index}"]`);
  };

  const groups = [];
  dayResults.forEach((result) => {
    const last = groups[groups.length - 1];
    if (!last || last.date !== result.date) groups.push({ date: result.date, items: [result] });
    else last.items.push(result);
  });

  return (
    <div className={styles.search} ref={popRef} role="dialog" aria-label={t('search.label')}>
      <div className={styles.searchBar}>
        <IconSearch />
        <input
          ref={inputRef}
          value={ui.query}
          placeholder={t('search.placeholder')}
          aria-label={t('search.label')}
          onChange={(event) => setQuery(event.target.value)}
        />
        {ui.query && (
          <button type="button" className={styles.clear} onClick={() => setQuery('')}>
            {t('search.clear')}
          </button>
        )}
      </div>
      <div className={styles.results}>
        {!ui.query.trim() && <p className={styles.hint}>{t('search.hint')}</p>}
        {ui.query.trim() && results.length === 0 && <p className={styles.hint}>{t('search.empty')}</p>}
        {groups.map((group) => {
          const date = fromKey(group.date);
          return (
            <div key={group.date} className={styles.group}>
              <div className={styles.groupTitle}>
                {formatWeekday(date, lang)} · {formatDateLabel(date, lang)}
              </div>
              {group.items.map((result) => (
                <button
                  key={result.task.id}
                  type="button"
                  className={styles.result}
                  onClick={() => openResult(result)}
                >
                  <strong className={result.task.done ? styles.struck : undefined} dir="auto">
                    <Highlight text={result.task.text} query={ui.query} />
                  </strong>
                </button>
              ))}
            </div>
          );
        })}
        {recurResults.length > 0 && (
          <div className={styles.group}>
            <div className={styles.groupTitle}>{t('recur.title')}</div>
            {recurResults.map((result) => (
              <button
                key={result.task.id}
                type="button"
                className={styles.result}
                onClick={() => openResult(result)}
              >
                <strong dir="auto">
                  <Highlight text={result.task.text} query={ui.query} />
                </strong>
                <span>{recurrenceLabel(result.item, t)}</span>
              </button>
            ))}
          </div>
        )}
        {listResults.length > 0 && (
          <div className={styles.group}>
            <div className={styles.groupTitle}>{t('search.lists')}</div>
            {listResults.map((result) => (
              <button
                key={result.task.id}
                type="button"
                className={styles.result}
                onClick={() => openResult(result)}
              >
                <strong className={result.task.done ? styles.struck : undefined} dir="auto">
                  <Highlight text={result.task.text} query={ui.query} />
                </strong>
                <span>
                  {result.pageName} · {result.listTitle}
                </span>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
